import React, { useEffect, useState } from 'react'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import SplitPanel from './SplitPanel.jsx'
import {
  getLinkListFromApi,
  getLocalPath,
  getMargins,
  getSharedSiteName,
} from '../../utility'
import { fetchData } from '../../data'
import { isLocal } from '../../../global/environments/index.js'

const normalizeText = (value) => {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object')
    return value.value || value.content || ''
  return ''
}

const resolveNode = (node, page) => {
  if (!node) return null

  if (typeof node.getData === 'function') {
    return node.getData()
  }

  if (node.$ref && page?.getContent) {
    const content = page.getContent(node.$ref)
    return typeof content?.getData === 'function' ? content.getData() : content
  }

  return node
}

const normalizeBody = (source) => {
  return normalizeText(
    source.body?.html?.value ||
      source.body?.html?.content ||
      source.body?.value ||
      source.body?.content ||
      source.body ||
      source.content?.html?.value ||
      source.content?.value ||
      source.content ||
      source.description ||
      source.copy
  )
}

const getPanelOrder = (source = {}) => {
  const metaValue = source.meta ?? source['cti:meta']

  const candidateValues = [
    typeof metaValue === 'object' && metaValue !== null
      ? (metaValue.order ?? metaValue.value)
      : metaValue,
    source.order,
  ]

  for (const value of candidateValues) {
    if (value === null || value === undefined || value === '') continue
    const numeric = Number.parseInt(`${value}`, 10)
    if (!Number.isNaN(numeric)) {
      return numeric
    }
  }

  return null
}

const sortPanelsByOrder = (panels = []) => {
  return panels
    .map((panel, index) => ({
      ...panel,
      _sourceIndex: index,
    }))
    .sort((left, right) => {
      const leftHasOrder = Number.isInteger(left.order)
      const rightHasOrder = Number.isInteger(right.order)

      if (leftHasOrder && rightHasOrder && left.order !== right.order) {
        return left.order - right.order
      }

      if (leftHasOrder && !rightHasOrder) return -1
      if (!leftHasOrder && rightHasOrder) return 1

      return left._sourceIndex - right._sourceIndex
    })
    .map(({ _sourceIndex, ...panel }) => panel)
}

const normalizePanel = (panel, page) => {
  const source = resolveNode(panel, page)
  if (!source || typeof source !== 'object') return null

  // CMS uses ButtonLinkCompound field for link/button data
  const buttonLinkCompound = Array.isArray(source.ButtonLinkCompound)
    ? source.ButtonLinkCompound
    : Array.isArray(source.links)
      ? source.links
      : []

  const filteredLinks = buttonLinkCompound.filter(
    (l) => l.Link && l.Link.length > 0
  )
  const buttons = filteredLinks
    .map((linkItem, index) => {
      const base = getLinkListFromApi([linkItem])[0]
      if (!base?.url || !base?.text) return null
      return {
        ...base,
        target: linkItem.target || '_self',
        type: linkItem.type,
        reversed: !!linkItem.reversed,
        display: linkItem.display,
        width: linkItem.width,
        analyticsAttribute: linkItem.analyticsAttribute,
      }
    })
    .filter(Boolean)

  const bgImage = source?.bgImage?._meta?.mirror?.path
    ? `${isLocal() ? '/cti-site' : ''}/binaries/${source.bgImage._meta.mirror.path}`
    : ''

  const imageSrc = source?.imageDetails?.image?._meta?.mirror?.path
    ? `${isLocal() ? '/cti-site' : ''}/binaries/${source?.imageDetails?.image?._meta?.mirror?.path}`
    : ''

  return {
    order: getPanelOrder(source),
    title: normalizeText(
      source.title || source.heading || source.headline || source.primaryHeading
    ),
    body: normalizeBody(source),
    bgColor:
      source.bgColor || source.backgroundColor || source.background || '',
    bgImage,
    bgImageOverlay: source?.bgImageOverlay || false,
    buttons,
    buttonAlignment:
      source.buttonAlignment || source.buttonRowAlignment || source.alignment,
    panelClassName: source.panelClassName || source.panelClass,
    innerClassName: source.innerClassName || source.innerClass,
    eyebrow: source.eyebrow || '',
    eyebrowColor: source.overlineColor || '',
    image: {
      ...source.imageDetails,
      src: imageSrc,
    },
  }
}

const hasPanelContent = (panel) =>
  !!panel &&
  !!(
    panel.title ||
    panel.body ||
    panel.bgColor ||
    panel.bgImage ||
    panel.bgImageOverlay ||
    panel.buttons?.length ||
    panel.panelClassName ||
    panel.innerClassName ||
    panel.eyebrow ||
    panel.image?.src
  )

const getPanelsFromData = (data, page) => {
  if (!data || typeof data !== 'object') return []

  const candidatePanels = Array.isArray(data.panels)
    ? data.panels
    : Array.isArray(data.splitPanels)
      ? data.splitPanels
      : Array.isArray(data.panelList)
        ? data.panelList
        : [data.leftPanel, data.rightPanel].filter(Boolean)

  return sortPanelsByOrder(
    candidatePanels
      .map((panel) => normalizePanel(panel, page))
      .filter(hasPanelContent)
  ).slice(0, 2)
}

const getItemsFromModel = (model, page) => {
  if (!model) return []

  if (typeof model.getItems === 'function') {
    return model.getItems() || []
  }

  if (model.$ref && page?.getContent) {
    const resolved = page.getContent(model.$ref)
    if (resolved && typeof resolved.getItems === 'function') {
      return resolved.getItems() || []
    }
  }

  return []
}

const getPanelsFromModels = (models, page) => {
  if (!models || typeof models !== 'object') return []

  const modelValues = Object.values(models)
  const paginationItems = modelValues.flatMap((value) =>
    getItemsFromModel(value, page)
  )

  const explicitPanels = [
    models.leftPanel,
    models.rightPanel,
    models.leftColumn,
    models.rightColumn,
    models.panel1,
    models.panel2,
  ].filter(Boolean)

  const arrayPanels = modelValues.flatMap((value) =>
    Array.isArray(value) ? value : []
  )

  const refPanels = modelValues.filter(
    (value) => value && typeof value === 'object' && value.$ref
  )

  const candidatePanels = [
    ...explicitPanels,
    ...paginationItems,
    ...arrayPanels,
    ...refPanels,
  ]

  return sortPanelsByOrder(
    candidatePanels
      .map((panel) => normalizePanel(panel, page))
      .filter(hasPanelContent)
  ).slice(0, 2)
}

const getScopePath = (scope = '') => {
  if (!scope || typeof scope !== 'string') return ''

  if (scope.startsWith('/content/documents/')) return scope

  const normalizedScope = scope.startsWith('/') ? scope.slice(1) : scope
  const sitePrefix = `${getSharedSiteName()}/`
  const siteScopedPath = normalizedScope.startsWith(sitePrefix)
    ? normalizedScope
    : `${sitePrefix}${normalizedScope}`

  return `/content/documents/${siteScopedPath}`
}

const SplitPanelWrapper = ({ page, component }) => {
  const models = component?.getModels?.() || {}
  const documentRef = models.document
  const document = documentRef && page?.getContent?.(documentRef)
  const canShowManageButton =
    document &&
    typeof document.getMeta === 'function' &&
    typeof document.getData === 'function'
  const data = document?.getData?.() || {}
  const _parameters = component?.getParameters?.() || {}
  const parameters = {
    ..._parameters,
    marginTop: _parameters?.marginTop || 'No Spacing',
    marginBottom: _parameters?.marginBottom || 'No Spacing',
  }
  const [scopePanels, setScopePanels] = useState([])

  useEffect(() => {
    const scope =
      parameters.scope ||
      parameters.document ||
      parameters.folder ||
      parameters.path ||
      ''

    const scopePath = getScopePath(scope)
    if (!scopePath) {
      setScopePanels([])
      return
    }

    let isMounted = true

    const fetchScopePanels = async () => {
      const itemsUrl = `${getLocalPath()}/halapi/documents?_scope=${scopePath}&_sort=cti:meta&_limit=2`
      const response = await fetchData(itemsUrl)
      const documents = response?._embedded?.documents || []
      const panels = documents
        .map((doc) => normalizePanel(doc, page))
        .filter(hasPanelContent)
        .slice(0, 2)

      if (isMounted) {
        setScopePanels(panels)
      }
    }

    fetchScopePanels()

    return () => {
      isMounted = false
    }
  }, [
    page,
    parameters.scope,
    parameters.document,
    parameters.folder,
    parameters.path,
  ])

  const cmsPanels = getPanelsFromData(data, page)
  const modelPanels = getPanelsFromModels(models, page)
  const panels = cmsPanels.length
    ? cmsPanels
    : scopePanels.length
      ? scopePanels
      : modelPanels

  return (
    <div
      className={`${getMargins(parameters.marginTop, parameters.marginBottom)}`}
    >
      <div className="u-posRelative">
        {canShowManageButton && <BrManageContentButton content={document} />}
      </div>
      <SplitPanel panels={panels} />
    </div>
  )
}

export default SplitPanelWrapper
